import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Input } from '../components/ui/input';
import { useToast } from '../hooks/use-toast';
import useAdminAuth from '../hooks/useAdminAuth';
import axios from 'axios';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { Check, X, DollarSign, Clock, Truck, Package, LogOut, Users, Trophy, MessageSquare, HelpCircle, Star, Compass, HardHat, Zap, MapPin as MapPinIcon, Camera, UserCheck, Settings, ChevronRight, Megaphone } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AdminDashboard = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const { logout, getAdminEmail } = useAdminAuth();
  const [pendingQuotes, setPendingQuotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const adminEmail = getAdminEmail();
  const [rejectionReasons, setRejectionReasons] = useState({});
  const [openGroup, setOpenGroup] = useState(null); // 'pilots' | 'backend' | null

  const closeGroup = () => setOpenGroup(null);
  const goTo = (path) => { setOpenGroup(null); navigate(path); };

  useEffect(() => {
    fetchPendingQuotes();
  }, []);

  const fetchPendingQuotes = async () => {
    try {
      const response = await axios.get(`${API}/admin/quotes/pending`);
      setPendingQuotes(response.data);
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to load pending quotes",
        variant: "destructive",
      });
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const handleApprove = async (quoteId, quoteAmount) => {
    try {
      const response = await axios.post(
        `${API}/admin/quotes/${quoteId}/approve?admin_email=${adminEmail}`
      );
      
      toast({
        title: "Quote Approved",
        description: `Quote for $${quoteAmount} approved and sent to shipper`,
      });
      fetchPendingQuotes();
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to approve quote",
        variant: "destructive",
      });
      console.error(error);
    }
  };

  const handleReject = async (quoteId) => {
    const reason = rejectionReasons[quoteId] || '';
    
    try {
      await axios.post(
        `${API}/admin/quotes/${quoteId}/reject?admin_email=${adminEmail}&reason=${encodeURIComponent(reason)}`
      );
      
      toast({
        title: "Quote Rejected",
        description: "Quote has been rejected successfully",
      });
      fetchPendingQuotes();
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to reject quote",
        variant: "destructive",
      });
      console.error(error);
    }
  };

  const handleRejectionReasonChange = (quoteId, value) => {
    setRejectionReasons(prev => ({ ...prev, [quoteId]: value }));
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Header />
        <div className="container mx-auto px-4 py-12 text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-red-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading pending quotes...</p>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />
      
      <div className="bg-[#1e3a5f] text-white py-6 sm:py-12">
        <div className="container mx-auto px-4 sm:px-6">
          <div className="mb-4 sm:mb-8">
            <h1 className="text-2xl sm:text-4xl font-bold mb-2 sm:mb-4">Admin Dashboard</h1>
            <p className="text-sm sm:text-lg text-gray-200">Manage your platform</p>
          </div>
          
          {/* Navigation Grid - compact on mobile */}
          <div className="grid grid-cols-3 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-2 sm:gap-4 mb-4 sm:mb-6">
            <Button
              onClick={() => navigate('/admin/loads')}
              variant="outline"
              className="bg-white/10 text-white border-white/40 hover:bg-white hover:text-[#1e3a5f] h-auto py-2 sm:py-4 flex flex-col items-center gap-1 sm:gap-2 text-center"
            >
              <Package className="w-5 h-5 sm:w-6 sm:h-6" />
              <span className="font-semibold text-xs sm:text-sm">Loads</span>
            </Button>
            <Button
              onClick={() => navigate('/admin/users')}
              variant="outline"
              className="bg-white/10 text-white border-white/40 hover:bg-white hover:text-[#1e3a5f] h-auto py-2 sm:py-4 flex flex-col items-center gap-1 sm:gap-2 text-center"
            >
              <Users className="w-5 h-5 sm:w-6 sm:h-6" />
              <span className="font-semibold text-xs sm:text-sm">Users</span>
            </Button>
            <Button
              onClick={() => navigate('/admin/active-quotes')}
              variant="outline"
              className="bg-white/10 text-white border-white/40 hover:bg-white hover:text-[#1e3a5f] h-auto py-2 sm:py-4 flex flex-col items-center gap-1 sm:gap-2 text-center"
              data-testid="admin-active-quotes-btn"
            >
              <DollarSign className="w-5 h-5 sm:w-6 sm:h-6" />
              <span className="font-semibold text-xs sm:text-sm">Quotes $</span>
            </Button>
            <Button
              onClick={() => navigate('/admin/transporter-listings')}
              variant="outline"
              className="bg-white/10 text-white border-white/40 hover:bg-white hover:text-[#1e3a5f] h-auto py-2 sm:py-4 flex flex-col items-center gap-1 sm:gap-2 text-center"
              data-testid="admin-transporter-listings-btn"
            >
              <Truck className="w-5 h-5 sm:w-6 sm:h-6" />
              <span className="font-semibold text-xs sm:text-sm">Trucks</span>
            </Button>
            <Button
              onClick={() => setOpenGroup('pilots')}
              variant="outline"
              className="bg-white/10 text-white border-white/40 hover:bg-white hover:text-[#1e3a5f] h-auto py-2 sm:py-4 flex flex-col items-center gap-1 sm:gap-2 text-center relative"
              data-testid="admin-pilots-group-btn"
            >
              <Compass className="w-5 h-5 sm:w-6 sm:h-6" />
              <span className="font-semibold text-xs sm:text-sm">Pilots ▾</span>
            </Button>
            <Button
              onClick={() => navigate('/admin/promo-codes')}
              variant="outline"
              className="bg-white/10 text-white border-white/40 hover:bg-white hover:text-[#1e3a5f] h-auto py-2 sm:py-4 flex flex-col items-center gap-1 sm:gap-2 text-center"
            >
              <Check className="w-5 h-5 sm:w-6 sm:h-6" />
              <span className="font-semibold text-xs sm:text-sm">Promos</span>
            </Button>
            <Button
              onClick={() => navigate('/admin/gallery')}
              variant="outline"
              className="bg-white/10 text-white border-white/40 hover:bg-white hover:text-[#1e3a5f] h-auto py-2 sm:py-4 flex flex-col items-center gap-1 sm:gap-2 text-center"
            >
              <Check className="w-5 h-5 sm:w-6 sm:h-6" />
              <span className="font-semibold text-xs sm:text-sm">Gallery</span>
            </Button>
            <Button
              onClick={() => navigate('/admin/competition')}
              variant="outline"
              className="bg-white/10 text-white border-white/40 hover:bg-white hover:text-[#1e3a5f] h-auto py-2 sm:py-4 flex flex-col items-center gap-1 sm:gap-2 text-center"
              data-testid="admin-competition-btn"
            >
              <Trophy className="w-5 h-5 sm:w-6 sm:h-6" />
              <span className="font-semibold text-xs sm:text-sm">Comp</span>
            </Button>
            <Button
              onClick={() => navigate('/admin/qa')}
              variant="outline"
              className="bg-white/10 text-white border-white/40 hover:bg-white hover:text-[#1e3a5f] h-auto py-2 sm:py-4 flex flex-col items-center gap-1 sm:gap-2 text-center"
              data-testid="admin-qa-btn"
            >
              <MessageSquare className="w-5 h-5 sm:w-6 sm:h-6" />
              <span className="font-semibold text-xs sm:text-sm">Q&A</span>
            </Button>
            <Button
              onClick={() => navigate('/admin/negotiations')}
              variant="outline"
              className="bg-white/10 text-white border-white/40 hover:bg-white hover:text-[#1e3a5f] h-auto py-2 sm:py-4 flex flex-col items-center gap-1 sm:gap-2 text-center"
              data-testid="admin-negotiations-btn"
            >
              <MessageSquare className="w-5 h-5 sm:w-6 sm:h-6" />
              <span className="font-semibold text-xs sm:text-sm">Negotiations</span>
            </Button>
            <Button
              onClick={() => setOpenGroup('backend')}
              variant="outline"
              className="bg-white/10 text-white border-white/40 hover:bg-white hover:text-[#1e3a5f] h-auto py-2 sm:py-4 flex flex-col items-center gap-1 sm:gap-2 text-center"
              data-testid="admin-backend-group-btn"
            >
              <Settings className="w-5 h-5 sm:w-6 sm:h-6" />
              <span className="font-semibold text-xs sm:text-sm">Back End ▾</span>
            </Button>
            <Button
              onClick={() => navigate('/admin/transactions')}
              variant="outline"
              className="bg-white/10 text-white border-white/40 hover:bg-white hover:text-[#1e3a5f] h-auto py-2 sm:py-4 flex flex-col items-center gap-1 sm:gap-2 text-center"
              data-testid="admin-transactions-btn"
            >
              <DollarSign className="w-5 h-5 sm:w-6 sm:h-6" />
              <span className="font-semibold text-xs sm:text-sm">Transactions</span>
            </Button>
            <Button
              onClick={() => navigate('/admin/reviews')}
              variant="outline"
              className="bg-white/10 text-white border-white/40 hover:bg-white hover:text-[#1e3a5f] h-auto py-2 sm:py-4 flex flex-col items-center gap-1 sm:gap-2 text-center"
              data-testid="admin-reviews-btn"
            >
              <Star className="w-5 h-5 sm:w-6 sm:h-6" />
              <span className="font-semibold text-xs sm:text-sm">Reviews</span>
            </Button>
            <Button
              onClick={() => navigate('/admin/teds-travels')}
              variant="outline"
              className="bg-white/10 text-white border-white/40 hover:bg-white hover:text-[#1e3a5f] h-auto py-2 sm:py-4 flex flex-col items-center gap-1 sm:gap-2 text-center"
              data-testid="admin-teds-btn"
            >
              <Camera className="w-5 h-5 sm:w-6 sm:h-6" />
              <span className="font-semibold text-xs sm:text-sm">Ted&apos;s Travels</span>
            </Button>
          </div>

          {/* Services Section */}
          <div className="mt-6 pt-6 border-t border-white/20">
            <h3 className="text-white/70 text-xs uppercase tracking-wider mb-3 font-semibold">Service Requests</h3>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
              <Button
                onClick={() => navigate('/admin/crane-requests')}
                variant="outline"
                className="bg-white/10 text-white border-white/40 hover:bg-white hover:text-[#1e3a5f] h-auto py-2 sm:py-3 flex flex-col items-center gap-1 text-center"
                data-testid="admin-crane-btn"
              >
                <HardHat className="w-5 h-5" />
                <span className="font-semibold text-xs">Cranes</span>
              </Button>
              <Button
                onClick={() => navigate('/admin/hotshots')}
                variant="outline"
                className="bg-white/10 text-white border-white/40 hover:bg-white hover:text-[#1e3a5f] h-auto py-2 sm:py-3 flex flex-col items-center gap-1 text-center"
                data-testid="admin-hotshots-btn"
              >
                <Zap className="w-5 h-5" />
                <span className="font-semibold text-xs">Hotshots</span>
              </Button>
              <Button
                onClick={() => navigate('/admin/route-surveys')}
                variant="outline"
                className="bg-white/10 text-white border-white/40 hover:bg-white hover:text-[#1e3a5f] h-auto py-2 sm:py-3 flex flex-col items-center gap-1 text-center"
                data-testid="admin-route-surveys-btn"
              >
                <MapPinIcon className="w-5 h-5" />
                <span className="font-semibold text-xs">Route Surveys</span>
              </Button>
            </div>
          </div>
          
          <div className="flex justify-end">
            <Button
              onClick={logout}
              variant="outline"
              className="bg-red-600/10 text-white border-red-400 hover:bg-red-600 hover:text-white text-xs sm:text-sm"
            >
              <LogOut className="w-4 h-4 mr-1 sm:mr-2" />
              Logout
            </Button>
          </div>
        </div>
      </div>

      <div className="container mx-auto px-4 sm:px-6 py-4 sm:py-8">
        {/* Admin Info */}
        <Card className="p-3 sm:p-4 mb-4 sm:mb-6">
          <div className="flex items-center justify-between">
            <div>
              <Label className="text-xs sm:text-sm">Logged in as:</Label>
              <p className="text-xs sm:text-sm text-gray-600 mt-1">{adminEmail}</p>
            </div>
          </div>
        </Card>

        {/* Stats */}
        <div className="grid grid-cols-3 gap-2 sm:gap-6 mb-4 sm:mb-8">
          <Card className="p-3 sm:p-6">
            <div className="flex flex-col sm:flex-row items-center sm:items-center gap-1 sm:gap-3 text-center sm:text-left">
              <Clock className="w-6 h-6 sm:w-8 sm:h-8 text-yellow-600" />
              <div>
                <p className="text-xl sm:text-2xl font-bold text-[#1e3a5f]">{pendingQuotes.length}</p>
                <p className="text-[10px] sm:text-sm text-gray-600">Pending</p>
              </div>
            </div>
          </Card>
          
          <Card className="p-3 sm:p-6">
            <div className="flex flex-col sm:flex-row items-center sm:items-center gap-1 sm:gap-3 text-center sm:text-left">
              <DollarSign className="w-6 h-6 sm:w-8 sm:h-8 text-green-600" />
              <div>
                <p className="text-lg sm:text-2xl font-bold text-[#1e3a5f]">
                  ${pendingQuotes.reduce((sum, q) => sum + (q.original_amount || q.amount), 0).toFixed(0)}
                </p>
                <p className="text-[10px] sm:text-sm text-gray-600">Value</p>
              </div>
            </div>
          </Card>
          
          <Card className="p-3 sm:p-6">
            <div className="flex flex-col sm:flex-row items-center sm:items-center gap-1 sm:gap-3 text-center sm:text-left">
              <Package className="w-6 h-6 sm:w-8 sm:h-8 text-blue-600" />
              <div>
                <p className="text-xl sm:text-2xl font-bold text-[#1e3a5f]">
                  {pendingQuotes.length}
                </p>
                <p className="text-[10px] sm:text-sm text-gray-600">To Review</p>
              </div>
            </div>
          </Card>
        </div>

        {/* Pending Quotes */}
        <div className="mb-4 flex items-center justify-between">
          <h2 className="text-2xl font-bold text-[#1e3a5f]">Pending Quotes</h2>
          <Button onClick={fetchPendingQuotes} variant="outline" size="sm">
            Refresh
          </Button>
        </div>

        {pendingQuotes.length === 0 ? (
          <Card className="p-12 text-center">
            <Check className="w-16 h-16 mx-auto text-green-600 mb-4" />
            <h3 className="text-xl font-semibold text-gray-700 mb-2">All caught up!</h3>
            <p className="text-gray-600">No quotes pending approval</p>
          </Card>
        ) : (
          <div className="space-y-6">
            {pendingQuotes.map((quote) => {
              const quoteAmount = quote.original_amount || quote.amount;

              return (
                <Card key={quote.id} className="p-6">
                  <div className="space-y-4">
                    {/* Quote Header */}
                    <div className="flex items-start justify-between border-b pb-4">
                      <div>
                        <h3 className="text-lg font-bold text-[#1e3a5f] mb-1">
                          Quote ID: {quote.id.slice(0, 8)}
                        </h3>
                        <p className="text-sm text-gray-600">Load ID: <a href={`/load/${quote.load_id}`} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">{quote.load_id.slice(0, 8)}</a></p>
                      </div>
                      <span className="px-3 py-1 bg-yellow-100 text-yellow-800 rounded-full text-sm font-semibold">
                        PENDING APPROVAL
                      </span>
                    </div>

                    {/* Quote Details */}
                    <div className="grid md:grid-cols-2 gap-4">
                      <div>
                        <p className="text-sm font-semibold text-gray-700 mb-1">Carrier</p>
                        <div className="flex items-center gap-2">
                          <Truck className="w-4 h-4 text-gray-500" />
                          <p className="text-gray-600">{quote.carrier_email}</p>
                        </div>
                      </div>

                      <div>
                        <p className="text-sm font-semibold text-gray-700 mb-1">Estimated Delivery</p>
                        <p className="text-gray-600">{quote.estimated_delivery_days} days</p>
                      </div>
                    </div>

                    {quote.message && (
                      <div>
                        <p className="text-sm font-semibold text-gray-700 mb-1">Message</p>
                        <p className="text-sm text-gray-600 bg-gray-50 p-3 rounded">{quote.message}</p>
                      </div>
                    )}

                    {/* Quote Amount */}
                    <div className="bg-blue-50 p-4 rounded-lg">
                      <div className="flex justify-between items-center">
                        <span className="font-bold text-[#1e3a5f]">Quote Amount:</span>
                        <span className="font-bold text-[#1e3a5f] text-xl">${quoteAmount.toFixed(2)}</span>
                      </div>
                      <p className="text-xs text-gray-600 mt-2">
                        This is the exact amount the shipper will pay
                      </p>
                    </div>

                    {/* Actions */}
                    <div className="flex flex-col sm:flex-row gap-3">
                      <Button
                        onClick={() => handleApprove(quote.id, quoteAmount)}
                        className="flex-1 bg-green-600 hover:bg-green-700 text-white"
                      >
                        <Check className="w-4 h-4 mr-2" />
                        Approve Quote
                      </Button>
                      
                      <div className="flex-1 space-y-2">
                        <Textarea
                          placeholder="Rejection reason (optional)"
                          value={rejectionReasons[quote.id] || ''}
                          onChange={(e) => handleRejectionReasonChange(quote.id, e.target.value)}
                          rows={1}
                          className="text-sm"
                        />
                        <Button
                          onClick={() => handleReject(quote.id)}
                          variant="outline"
                          className="w-full border-red-600 text-red-600 hover:bg-red-50"
                        >
                          <X className="w-4 h-4 mr-2" />
                          Reject Quote
                        </Button>
                      </div>
                    </div>
                  </div>
                </Card>
              );
            })}
          </div>
        )}
      </div>
      
      {openGroup && (
        <div
          className="fixed inset-0 z-[70] bg-black/60 flex items-end sm:items-center justify-center p-0 sm:p-4"
          onClick={closeGroup}
          data-testid={`admin-group-${openGroup}-backdrop`}
        >
          <div
            className="bg-white w-full sm:max-w-md rounded-t-2xl sm:rounded-2xl p-5 shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-lg font-bold text-[#1e3a5f]">
                {openGroup === 'pilots' ? 'Pilots' : 'Back End'}
              </h3>
              <button
                onClick={closeGroup}
                className="text-gray-500 hover:text-gray-800"
                data-testid={`admin-group-${openGroup}-close`}
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {openGroup === 'pilots' ? (
              <div className="space-y-2">
                {[
                  { path: '/admin/pilots/availability', title: 'Pilot Map', desc: 'View & edit live pilot map locations', icon: Compass, testid: 'admin-group-pilot-map' },
                  { path: '/admin/pilots/approvals', title: 'Pilot Approvals', desc: 'Review & bulk approve new pilots', icon: UserCheck, testid: 'admin-group-pilot-approvals' },
                  { path: '/admin/pilot-registrations', title: 'Pilot Registrations', desc: 'All pilot registration records', icon: Compass, testid: 'admin-group-pilot-regs' },
                  { path: '/admin/pilots', title: 'Pilot Service Requests', desc: 'Requests for pilot / steerer bookings', icon: Compass, testid: 'admin-group-pilot-requests' },
                  { path: '/admin/pilots/enquiries', title: 'Pilot Enquiries', desc: 'Enquiries from pilots & agents', icon: MessageSquare, testid: 'admin-group-pilot-enquiries' },
                  { path: '/admin/pilot-accreditations', title: 'Pilot Courses', desc: 'Course accreditation records', icon: Compass, testid: 'admin-group-pilot-courses' },
                ].map((it) => {
                  const Icon = it.icon;
                  return (
                    <button
                      key={it.path}
                      onClick={() => goTo(it.path)}
                      className="w-full text-left px-4 py-3 rounded-lg border border-gray-200 hover:border-[#1e3a5f] hover:bg-blue-50 transition flex items-center gap-3"
                      data-testid={it.testid}
                    >
                      <span className="w-9 h-9 rounded-lg bg-blue-100 text-[#1e3a5f] flex items-center justify-center flex-shrink-0">
                        <Icon className="w-5 h-5" />
                      </span>
                      <span className="flex-1 min-w-0">
                        <span className="block font-semibold text-[#1e3a5f]">{it.title}</span>
                        <span className="block text-xs text-gray-500">{it.desc}</span>
                      </span>
                      <ChevronRight className="w-4 h-4 text-gray-400 flex-shrink-0" />
                    </button>
                  );
                })}
              </div>
            ) : (
              <div className="space-y-2">
                {[
                  { path: '/admin/analytics', title: 'Analytics', desc: 'Traffic & platform stats', icon: Truck, testid: 'admin-group-backend-analytics' },
                  { path: '/admin/content', title: 'CMS', desc: 'Edit landing page copy & blocks', icon: Check, testid: 'admin-group-backend-cms' },
                  { path: '/admin/announcements', title: 'Announcements', desc: 'Red flip-card banner on the hero logo', icon: Megaphone, testid: 'admin-group-backend-announcements' },
                  { path: '/admin/faqs', title: 'FAQs', desc: 'Add / edit / reorder FAQ entries', icon: HelpCircle, testid: 'admin-group-backend-faqs' },
                ].map((it) => {
                  const Icon = it.icon;
                  return (
                    <button
                      key={it.path}
                      onClick={() => goTo(it.path)}
                      className="w-full text-left px-4 py-3 rounded-lg border border-gray-200 hover:border-[#1e3a5f] hover:bg-slate-50 transition flex items-center gap-3"
                      data-testid={it.testid}
                    >
                      <span className="w-9 h-9 rounded-lg bg-slate-100 text-[#1e3a5f] flex items-center justify-center flex-shrink-0">
                        <Icon className="w-5 h-5" />
                      </span>
                      <span className="flex-1 min-w-0">
                        <span className="block font-semibold text-[#1e3a5f]">{it.title}</span>
                        <span className="block text-xs text-gray-500">{it.desc}</span>
                      </span>
                      <ChevronRight className="w-4 h-4 text-gray-400 flex-shrink-0" />
                    </button>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      )}

      <Footer />
    </div>
  );
};

export default AdminDashboard;
